import { TeamComparisonHolder, TeamHolder } from '@pages/team/models/team.models'
import { Team } from '@core/models/team.models'
import { DualHeader, PLACEHOLDER_IMAGES } from '@mediacoach/ui'
import { findSeasonAndCompetitionByID } from '@core/utils/season.utils'
import { Competition, Season } from '@core/models/season-competition.models'
import { findStaticUrl, parseStatic } from '@core/utils/assets.utils'
import { StaticOrientation } from '@core/enums/statics.enum'
import { AGGREGATIONS_AVERAGE_SELECTABLE_MODE } from '@features/aggregations/constants/aggregation.constants'
import { DualHeaderHolder } from '@core/models/common.models'
import { MatchTeam } from '@core/models/match.models'
import { AggregationSelectable } from '@features/aggregations/models/aggregations.models'

export const resolveTeamDualHeaderBlock = (
  team: Team | MatchTeam,
  aggregation: AggregationSelectable,
  season: Season,
  competition: Competition
): DualHeaderHolder => {
  const isAgainstCompetition = aggregation.id === 'CompetitionAverage'
  const isAverage = aggregation.id === 'Average' || aggregation.id === 'CompetitionAverage'
  const text = isAgainstCompetition ? competition.officialName : (team.abbreviation ?? team.shortName)
  const image = isAgainstCompetition
    ? findStaticUrl(competition?.statics, { size: 'Small', orientation: 'Portrait' })
    : parseStatic(team.statics || [], StaticOrientation.Portrait)

  const placeholder = isAgainstCompetition ? PLACEHOLDER_IMAGES.COMPETITION : PLACEHOLDER_IMAGES.TEAM
  const subtitleImagePlaceholder = PLACEHOLDER_IMAGES.COMPETITION
  const subtitleImage = findStaticUrl(competition?.statics, { size: 'Small', orientation: 'Landscape' })
  const subtitle = isAverage ? AGGREGATIONS_AVERAGE_SELECTABLE_MODE.text : aggregation.text
  const pills = [season.beautifiedName, subtitle]

  return {
    text,
    image,
    placeholder,
    pills,
    subtitleImagePlaceholder,
    subtitleImage
  }
}

const buildTeamComparisonDualHeader = (
  comparison: TeamComparisonHolder,
  aggregation: AggregationSelectable,
  seasons: Season[]
): Partial<DualHeader> => {
  if (comparison?.teamA && comparison?.teamB) {
    const leftContext = findSeasonAndCompetitionByID(
      seasons,
      comparison?.teamA?.seasonId,
      comparison?.teamA?.competitionId
    )
    const rightContext = findSeasonAndCompetitionByID(
      seasons,
      comparison?.teamB?.seasonId,
      comparison?.teamB?.competitionId
    )

    const leftBlock = resolveTeamDualHeaderBlock(
      comparison.teamA,
      aggregation,
      leftContext.season,
      leftContext.competition
    )
    const rightBlock = resolveTeamDualHeaderBlock(
      comparison.teamB,
      aggregation,
      rightContext.season,
      rightContext.competition
    )

    return {
      leftText: leftBlock.text,
      leftImage: leftBlock.image,
      leftImagePlaceholder: leftBlock.placeholder,
      leftPills: leftBlock.pills,
      leftSubtitleImage: leftBlock.subtitleImage,
      leftSubtitleImagePlaceholder: leftBlock.subtitleImagePlaceholder,
      rightText: rightBlock.text,
      rightImage: rightBlock.image,
      rightPills: rightBlock.pills,
      rightSubtitleImage: rightBlock.subtitleImage,
      rightSubtitleImagePlaceholder: rightBlock.subtitleImagePlaceholder
    }
  }
}
const buildSingleTeamDualHeader = (
  team: Team,
  leftAggregation: AggregationSelectable,
  rightAggregation: AggregationSelectable,
  seasons: Season[]
) => {
  if (team) {
    const { season, competition } = findSeasonAndCompetitionByID(seasons, team.seasonId, team.competitionId)

    const leftBlock = resolveTeamDualHeaderBlock(team, leftAggregation, season, competition)
    const rightBlock = resolveTeamDualHeaderBlock(team, rightAggregation, season, competition)

    return {
      leftText: leftBlock.text,
      leftImage: leftBlock.image,
      leftImagePlaceholder: leftBlock.placeholder,
      leftPills: leftBlock.pills,
      leftSubtitleImage: leftBlock.subtitleImage,
      leftSubtitleImagePlaceholder: leftBlock.subtitleImagePlaceholder,
      rightText: rightBlock.text,
      rightImage: rightBlock.image,
      rightPills: rightBlock.pills,
      rightSubtitleImage: rightBlock.subtitleImage,
      rightSubtitleImagePlaceholder: rightBlock.subtitleImagePlaceholder,
      rightTriggerIcon: rightAggregation.id !== 'CompetitionAverage' ? 'loop' : undefined
    }
  }
}

export const buildTeamDualHeader = (
  holder: TeamHolder,
  leftAggregation: AggregationSelectable,
  rightAggregation: AggregationSelectable,
  aggregation: AggregationSelectable,
  isVsEnabled: boolean,
  seasons: Season[]
): Partial<DualHeader> => {
  const isComparisonCriteria = isVsEnabled && !!aggregation
  const isNotComparisonCriteria = !isVsEnabled && !!leftAggregation && !!rightAggregation
  if (holder && seasons?.length && (isComparisonCriteria || isNotComparisonCriteria)) {
    if (isVsEnabled && holder?.comparison?.teamB) {
      return buildTeamComparisonDualHeader(holder.comparison, aggregation, seasons)
    }

    return buildSingleTeamDualHeader(holder.team, leftAggregation, rightAggregation, seasons)
  }
}
